import { Component, OnDestroy, ViewChild, OnInit, ElementRef, NgZone, ViewEncapsulation } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs/internal/Subscription';
import { DrRequestInfo } from '../../../models/dr-request-info';

import { Endpoint, HelpIndex, MessageType, UpdateStatusType, ErrorCode, FilterAttribute, FilterOperator } from '../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, FilterTemplateType, GenericColDef, GridContext, SortByFilter } from '../../../models/grid-context';
import { JobHistoryTask, OperationState, TaskStatus, JobHistoryTaskStatusTitle } from '../../../models/task';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { ToggleItem } from '../../../models/toggle-item';
import { DrFilter } from '../../../models/dr-filter';
import * as _ from 'underscore';
import { DownloadService } from '../../../services/download/download.service';

@Component({
    standalone: false,
    selector: 'res-job-history',
    templateUrl: './job-history.component.html',
    styleUrls: ['./job-history.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class JobHistoryComponent implements OnDestroy, OnInit {
    @ViewChild(GenericGridComponent, { static: true }) jobHistoryGrid: GenericGridComponent;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

    automationId = 'job-history';
    gridName = 'Job History';
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.realmManager_listRealmTasks,
        responseName: 'tasks',
        data: { count: 0, startIndex: 0, filters: [], contextHandle: 'super_system_customer' },
    });
    isGridBusy = false;
    isGridBusyMessage = 'Loading ...';
    displayOnlyErrors = false;
    areAllStatesSelected = true;
    statusList: ToggleItem[];
    filterList: Array<DrFilter>;
    debounceFilter = _.debounce(this.filterAfterBounce, 1000);
    isSyntaxErrorsIncluded = true;
    tooltip = `Please select 'Errors' to download the log`;
    downloadTooltip = '';

    private entityChangedEvent: Subscription;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Operation Type',
            automationId: this.automationId,
            lockVisible: true,
            field: 'operationType',
            minWidth: 60,
            initialWidth: 190,
            filterType: FilterTemplateType.comboBox,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            filterAttributeName: "operation",
            suppressMovable: true,
        },
        {
            headerName: 'Operation Status / Description',
            automationId: this.automationId,
            field: 'description',
            minWidth: 60,
            initialWidth: 300,
            filterType: FilterTemplateType.freeform,
            cellRenderer: 'gridWorkbasketActionsCellRenderer',
            serviceTierFilter: true,
            filterAttributeName: "description",
            suppressMovable: true,
        },
        {
            headerName: 'Error Code',
            automationId: this.automationId,
            field: 'systemError.errorCode',
            minWidth: 60,
            initialWidth: 140,
            filterType: FilterTemplateType.comboBox,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Error Message',
            automationId: this.automationId,
            field: 'systemError.errorMessage',
            minWidth: 60,
            initialWidth: 170,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Request Identifier',
            automationId: this.automationId,
            field: 'systemError.requestIdentifier',
            minWidth: 30,
            initialWidth: 85,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'User',
            automationId: this.automationId,
            field: 'owner',
            minWidth: 60,
            initialWidth: 100,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Organization',
            automationId: this.automationId,
            minWidth: 60,
            initialWidth: 100,
            field: 'orgName',
            filterType: FilterTemplateType.comboBox,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            serviceTierFilter: true,
            filterAttributeName: "org",
            suppressMovable: true,
        },
        {
            headerName: 'Project',
            automationId: this.automationId,
            field: 'projectName',
            minWidth: 60,
            initialWidth: 125,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.disabledLinkLabel,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Start Time',
            automationId: this.automationId,
            minWidth: 60,
            initialWidth: 140,
            cellAlignDirection: AlignDirection.right,
            filterType: FilterTemplateType.date,
            field: 'dateStarted',
            defaultSortBy: SortByFilter.NAME,
            cellTemplateType: CellTemplateType.date,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Error Time',
            automationId: this.automationId,
            minWidth: 60,
            initialWidth: 140,
            cellAlignDirection: AlignDirection.right,
            field: 'systemError.timeOfFailureFormatted',
            filterType: FilterTemplateType.date,
            cellTemplateType: CellTemplateType.date,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'End Time',
            automationId: this.automationId,
            minWidth: 60,
            initialWidth: 140,
            cellAlignDirection: AlignDirection.right,
            field: 'dateCompleted',
            filterType: FilterTemplateType.date,
            cellTemplateType: CellTemplateType.date,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Duration',
            automationId: this.automationId,
            minWidth: 30,
            initialWidth: 70,
            cellAlignDirection: AlignDirection.right,
            field: 'secondsElapsed',
            cellTemplateType: CellTemplateType.computedDuration,
        }
    );

    gridContext = new GridContext({
        inlineGridFilter: true,
        isFilterNameChangeable: true,
        sortTypeEnabled: false,
        sortEnabled: false,
        sortBy: SortByFilter.NAME
    });

    constructor(private commonService: CommonService, private restService: RestService, private ngZone: NgZone, private downloadService: DownloadService) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.TASK) {
                this.jobHistoryGrid.gridContext.loadAndFilter = true;
                this.commonService.suppressGridSpinnerIfPoller = true;
                this.jobHistoryGrid.loadData();
            }
        });
    }

    ngOnInit(): void {
        this.displayOnlyErrors = false;
        this.areAllStatesSelected = true;
        this.isSyntaxErrorsIncluded = false;
        this.requestInfo.data.filters.push({ attribute: FilterAttribute.SYNTAXERROR, operator: FilterOperator.EQUALS, value: 'false' });
        this.filterList = [];
        this.downloadTooltip = this.tooltip;
        this.statusList = this.commonService.getEnumNames(JobHistoryTaskStatusTitle).sort().map(t =>
            new ToggleItem({
                label: t,
                isChecked: true
            }),
        );
        const promiseList = [];
        const errorCodeColDef = this.columnDefs.find(f => f.field === 'systemError.errorCode');
        errorCodeColDef.filterItems = this.commonService.getEnumNames(ErrorCode).sort();
        promiseList.push(this.restService.post(Endpoint.realmManager_listOperationTypes, { contextHandle: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser?.importedFromSystemUser ? 'super_system_customer' : '', count: 0, startIndex: 0, filters: [] }).then(response => {
            const operationTypeColDef = this.columnDefs.find(f => f.field === 'operationType');
            if (response?.workbasketTypes && operationTypeColDef) {
                operationTypeColDef.filterItems = response.workbasketTypes.sort();
            }
        }));

        promiseList.push(this.restService.post(Endpoint.realmManager_listOrganizations, { count: 0, startIndex: 0, filters: [], systemScope: true }).then(response => {
            const orgNameColDef = this.columnDefs.find(f => f.field === 'orgName');
            if (response?.organizations && orgNameColDef) {
                orgNameColDef.filterItems = response.organizations.map(q => q.name).sort();
            }
        }));

        Promise.all(promiseList).then(() => {
            if (this.jobHistoryGrid) {
                this.jobHistoryGrid.setColumnDefs(this.columnDefs);
            }
        });
    }

    responseCallbackHandler = (response: any) => {
        const tasks = new Array<JobHistoryTask>();
        response.tasks.forEach(element => {
            const task = new JobHistoryTask(element);
            tasks.push(task);
        });
        response.tasks = tasks;
        this.jobHistoryGrid.refreshColumns();
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode?.data) {
            if (action === CellActionType.linkLabelClick) {
                this.openProjectInNewSession(rowNode.data.projectHandle);
            } else if (action === CellActionType.workbasketIcon) {
                if (rowNode.data.systemError?.errorMessage) {
                    this.commonService.openErrorModal(rowNode.data.systemError.errorMessage, 'Task Failed', false);
                } else {
                    this.iconClick(rowNode.data);
                }
            }
        }
    }

    iconClick(task: JobHistoryTask): void {
        if (task && (task.status === TaskStatus.FAILURE || task.status === TaskStatus.PAUSED_ON_FAILURE)) {
            this.restService.post(Endpoint.taskManager_getTasks, {
                taskHandles: [task.workbasketHandle],
                includeDrDebug: true,
                includeResourceStatistics: true,
                contextHandle: task.projectHandle
            }).then(response => {
                const error = response && response.tasks && response.tasks[0] && response.tasks[0].extendedStatus ?
                    response.tasks[0].extendedStatus : 'Error information is unavailable.';
                this.commonService.openErrorModal(error, 'Task Failed', false);
            });
        } else if (task && task.isDownloadType()) {
            this.download(task);
        } else if (task && task.status === TaskStatus.SUCCESS && task.operationState === OperationState.WARNING) {
            this.commonService.openConfirmationInfoModal('Task completed with Warnings/Errors. Download the Warnings Report?',
                'Download Report', 'Download Report (.csv)', 'Cancel').then(result => {
                    if (result.modalResult) {
                        this.download(task);
                    }
                });
        }
    }

    download(task: JobHistoryTask): void {
        this.downloadService.download(task, this.downloadLink);
    }

    openProjectInNewSession(prjHandle: string): void {
        this.commonService.updateAppContextAndStoreIt(prjHandle);
        window.open(window.location.href, '_blank');
    }

    setDisplayErrors(value: boolean) {
        if (this.displayOnlyErrors !== value) {
            this.displayOnlyErrors = value;
            this.downloadTooltip = this.displayOnlyErrors ? '' : this.tooltip;
            this.requestInfo.data.filters = this.requestInfo.data.filters.filter(f => f.attribute !== FilterAttribute.ERROREXIST);
            this.jobHistoryGrid.filters = this.jobHistoryGrid.filters.filter(f => f.attribute !== FilterAttribute.ERROREXIST);
            if (this.displayOnlyErrors) {
                const newFilter = new DrFilter({ attribute: FilterAttribute.ERROREXIST, value: 'true' });
                this.requestInfo.data.filters.push(newFilter);
            }
            this.filterAfterBounce();
        }
    }

    onIncludeSyntaxErrorsChanged() {
        this.requestInfo.data.filters = this.requestInfo.data.filters.filter(f => f.attribute !== FilterAttribute.SYNTAXERROR);
        this.jobHistoryGrid.filters = this.jobHistoryGrid.filters.filter(f => f.attribute !== FilterAttribute.SYNTAXERROR);
        let newFilter;
        if (!this.isSyntaxErrorsIncluded) {
            newFilter = { attribute: FilterAttribute.SYNTAXERROR, operator: FilterOperator.EQUALS, value: this.isSyntaxErrorsIncluded?.toString() };
            this.requestInfo.data.filters.push(newFilter);
        }
        this.filterAfterBounce();
    }

    onAllStatesSelectedClicked() {
        if (!this.areAllStatesSelected) {
            this.areAllStatesSelected = true;
            this.statusList.forEach(s => s.isChecked = true);
            this.requestInfo.data.filters = this.requestInfo.data.filters.filter(f => f.attribute !== FilterAttribute.OPERATIONSTATE);
            this.jobHistoryGrid.filters = this.jobHistoryGrid.filters.filter(f => f.attribute !== FilterAttribute.OPERATIONSTATE);
            this.filterAfterBounce();
        }
    }

    onFilterToggleChanged(changedToggle: ToggleItem): void {
        let newFilter;
        this.requestInfo.data.filters = this.requestInfo.data.filters.filter(f => f.attribute !== FilterAttribute.OPERATIONSTATE);
        this.jobHistoryGrid.filters = this.jobHistoryGrid.filters.filter(f => f.attribute !== FilterAttribute.OPERATIONSTATE);
        if (changedToggle) {
            const checkedItems = this.statusList.filter(s => s.isChecked === true);
            if (checkedItems?.length < this.statusList.length) {
                const values = checkedItems.map(i => Object.keys(JobHistoryTaskStatusTitle)[Object.values(JobHistoryTaskStatusTitle).indexOf(i.label as any)]).join(',');
                newFilter = { attribute: FilterAttribute.OPERATIONSTATE, operator: FilterOperator.EQUALS, value: values };
            } else {
                this.areAllStatesSelected = true;
            }
        } else {
            newFilter = { attribute: FilterAttribute.OPERATIONSTATE, operator: FilterOperator.EQUALS, value: '' };
        }
        if (newFilter) {
            this.requestInfo.data.filters.push(newFilter);
            this.areAllStatesSelected = false;
        }
        this.debounceFilter();
    }

    filterAfterBounce(): void {
        this.requestInfo.data.startIndex = 0;
        this.jobHistoryGrid.loadData();
    }

    downloadSystemErrorReport(): void {
        const endpoint = Endpoint.realmManager_getSystemErrorsDownloadURL;
        const postParams = {
            filters: this.requestInfo.data.filters
        };
        this.restService.post(endpoint, postParams).then(response => {
            if (response) {
                this.commonService.unzipFile(response.url, this.downloadLink.nativeElement, 'SystemErrorsReport');
            }
        });
    }

    openHelp = (): void => {
        this.commonService.openHelp(HelpIndex.sys_job_history_dlg);
    }

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
    }
}
